<app-card-widget title="Information" subtitle="{{ (invoice$ | async)?.number }} / {{ (invoice$ | async)?.new_number }}" *ngIf="invoice$ | async">

  <ng-container *ngIf="invoice$ | async as data">
    <ng-container *ngIf="editablePolicy(data) as policy">
    <!--
      Every field's label floats, so an empty field shows its label whole, as
      a filled one does: unfloated beside the datepicker toggle, "Original
      Invoice Date" did not fit its column. The information-form styles let a
      message that wraps push the field below down instead of running over it.
    -->
    <form [formGroup]="form" id="invoice-info-v2-form" class="information-form" (ngSubmit)="save()" novalidate>

      <div *ngIf="reloadMessage" class="banner banner-warning">
        <span class="banner-text">{{ reloadMessage }}</span>
        <button type="button" mat-button (click)="reload()">Reload</button>
      </div>
      <div *ngIf="stateLockedMessage" class="banner banner-error">
        <span class="banner-text">{{ stateLockedMessage }}</span>
      </div>
      <div *ngIf="bannerFieldErrors.length > 0" class="banner banner-error">
        <div class="banner-text">
          <div *ngFor="let line of bannerFieldErrors" class="banner-line">{{ line }}</div>
        </div>
      </div>

      <div class="card-section-container">
        <div class="card-section">

          <app-text label="Bank Account" *ngIf="!edited || !policy.iban">{{ data.iban ? (data.iban | ibanFormatter) : placeholder }}</app-text>

          <!--
            A changed IBAN that fails the browser's check says so once touched.
            The server's refusal shows instead until the handler edits the
            field, which clears it, so the two never show at once.
          -->
          <mat-form-field floatLabel="always" *ngIf="edited && policy.iban" [class.field-error]="hasFieldError('iban')">
            <input matInput placeholder="Bank Account" formControlName="iban" [matAutocomplete]="ibanAuto" />
            <mat-hint *ngIf="hasFieldError('iban')" class="error-hint">{{ getFieldError('iban') }}</mat-hint>
            <mat-error *ngIf="!hasFieldError('iban')" class="error-hint">{{ ibanInvalidMessage }}</mat-error>
          </mat-form-field>
          <mat-autocomplete #ibanAuto="matAutocomplete">
            <ng-container *ngIf="bankAccounts$ | async as bankAccounts">
              <mat-option *ngFor="let account of bankAccounts" [value]="account.iban">
                {{ account.iban | ibanFormatter }} {{ account.verified_at ? '' : '(not verified)' }}
              </mat-option>
            </ng-container>
          </mat-autocomplete>

          <app-text label="Express Payment" *ngIf="!edited || !policy.express_payment" [ngSwitch]="data.express_payment">
            <span *ngSwitchCase="true">Yes</span>
            <span *ngSwitchDefault>No</span>
            <div *ngIf="showsClientExpressRequest(data)" class="express-request-note">{{ clientExpressRequestNote }}</div>
          </app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.express_payment" [class.field-error]="hasFieldError('express_payment')">
            <mat-select placeholder="Express Payment" formControlName="express_payment">
              <mat-option *ngFor="let option of expressPaymentChoices" [value]="option.value">{{ option.label }}</mat-option>
            </mat-select>
            <mat-hint *ngIf="hasFieldError('express_payment')" class="error-hint">{{ getFieldError('express_payment') }}</mat-hint>
            <!-- A field holds one start-aligned hint, so the server's message replaces this one. -->
            <mat-hint *ngIf="!hasFieldError('express_payment') && showsClientExpressRequest(data)" class="express-request-hint">{{ clientExpressRequestNote }}</mat-hint>
          </mat-form-field>

          <app-text label="Source"><app-source-name [sourceId]="data.source_id"></app-source-name></app-text>
          <app-text label="Source ID" *ngIf="data.correlation_id">{{ data.correlation_id }}</app-text>

        </div>

        <div class="card-section">

          <app-text label="Created">{{ data.created_at | date }}</app-text>

          <app-text label="Original Invoice Number" *ngIf="!edited || !policy.number">{{ data.number || placeholder }}</app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.number" [class.field-error]="hasFieldError('number')">
            <input matInput placeholder="Original Invoice Number" formControlName="number" />
            <mat-hint *ngIf="hasFieldError('number')" class="error-hint">{{ getFieldError('number') }}</mat-hint>
          </mat-form-field>

          <app-text label="Original Invoice Date" *ngIf="!edited || !policy.date">{{ data.date ? (data.date | date) : placeholder }}</app-text>

          <!--
            Optional: an empty field is never sent, since the server cannot
            clear a date. A value the field refuses itself (unreadable, before
            1.1.2000, or past the picker's limit) says why once touched. The
            server's refusal shows instead until the handler edits the field,
            which clears it, so the two never show at once. Due Date below
            works alike, without an upper limit.
          -->
          <mat-form-field floatLabel="always" *ngIf="edited && policy.date" [class.field-error]="hasFieldError('date')">
            <input matInput [matDatepicker]="datePicker" [min]="dateFields.earliest('date')" [max]="latestInvoiceDate()" placeholder="Original Invoice Date" formControlName="date" />
            <mat-datepicker-toggle matSuffix [for]="datePicker"></mat-datepicker-toggle>
            <mat-datepicker #datePicker></mat-datepicker>
            <mat-hint *ngIf="hasFieldError('date')" class="error-hint">{{ getFieldError('date') }}</mat-hint>
            <mat-error *ngIf="!hasFieldError('date')" class="error-hint">{{ dateFields.message('date') }}</mat-error>
          </mat-form-field>

          <app-text label="Due Date" *ngIf="!edited || !policy.due_date">{{ data.due_date ? (data.due_date | date) : placeholder }}</app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.due_date" [class.field-error]="hasFieldError('due_date')">
            <input matInput [matDatepicker]="dueDatePicker" [min]="dateFields.earliest('due_date')" placeholder="Due Date" formControlName="due_date" />
            <mat-datepicker-toggle matSuffix [for]="dueDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #dueDatePicker></mat-datepicker>
            <mat-hint *ngIf="hasFieldError('due_date')" class="error-hint">{{ getFieldError('due_date') }}</mat-hint>
            <mat-error *ngIf="!hasFieldError('due_date')" class="error-hint">{{ dateFields.message('due_date') }}</mat-error>
          </mat-form-field>

          <app-text label="Amount" *ngIf="!edited || !policy.amount">{{ data.amount?.amount ? (data.amount?.amount | currency:'EUR') : placeholder }}</app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.amount" [class.field-error]="hasFieldError('amount')">
            <input matInput type="number" placeholder="Amount" formControlName="amount" />
            <mat-hint *ngIf="hasFieldError('amount')" class="error-hint">{{ getFieldError('amount') }}</mat-hint>
          </mat-form-field>

          <app-text label="Original Reference Number" *ngIf="!edited || !policy.original_reference_number">{{ data.original_reference_number || placeholder }}</app-text>

          <!--
            The reference the client printed on its sales invoice. Editable
            wherever Bank Account is, through WaitingFinalPayment, so it can
            be entered before the final payment. Sent only when changed; the
            server normalises it and shows its refusal under the field.
            Marked required while the client's Original reference payments
            is on, since its transfers then need it. The mark only informs:
            it adds no validator, so an empty field never disables Save (nor
            aria-required, which matInput owns from that validator); the
            hint says why, and matInput links it by aria-describedby.
          -->
          <mat-form-field floatLabel="always" *ngIf="edited && policy.original_reference_number" [class.field-error]="hasFieldError('original_reference_number')">
            <input
              matInput
              [placeholder]="originalReferenceRequired(data) ? 'Original Reference Number *' : 'Original Reference Number'"
              formControlName="original_reference_number"
            />
            <mat-hint *ngIf="hasFieldError('original_reference_number')" class="error-hint">{{ getFieldError('original_reference_number') }}</mat-hint>
            <mat-hint *ngIf="!hasFieldError('original_reference_number') && originalReferenceRequired(data)" class="required-hint">{{ originalReferenceRequiredNote }}</mat-hint>
          </mat-form-field>

        </div>

        <div class="card-section">

          <app-text label="Corona Invoice Number" *ngIf="!edited || !policy.new_invoice_number">{{ data.new_number ?? placeholder }}</app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.new_invoice_number" [class.field-error]="hasFieldError('new_invoice_number')">
            <input matInput type="number" placeholder="Corona Invoice Number" formControlName="new_invoice_number" />
            <mat-hint *ngIf="hasFieldError('new_invoice_number')" class="error-hint">{{ getFieldError('new_invoice_number') }}</mat-hint>
          </mat-form-field>

          <app-text label="Corona Reference Number" *ngIf="!edited || !policy.new_reference_number">{{ data.new_reference_number || placeholder }}</app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.new_reference_number" [class.field-error]="hasFieldError('new_reference_number')">
            <input matInput placeholder="Reference Number" formControlName="new_reference_number" />
            <mat-hint *ngIf="hasFieldError('new_reference_number')" class="error-hint">{{ getFieldError('new_reference_number') }}</mat-hint>
          </mat-form-field>

          <app-text label="Netvisor ID">{{ data.external_id || placeholder }}</app-text>

          <!--
            Shown only for an invoice whose configuration is a subfinancing
            provider; the row's presence is itself the signal that this
            invoice has a subfinancing direction at all. "Not sent" is a real
            state here, not a missing value, so it is spelled out rather than
            rendered as the placeholder.
          -->
          <app-text label="Sent to Subfinancing" *ngIf="data.subfinancing">
            {{ data.subfinancing.sent_at ? (data.subfinancing.sent_at | date) : 'Not sent' }}
          </app-text>

          <app-text label="Lead Source" *ngIf="!edited || !policy.lead_source_id">{{ data.lead_source?.name || placeholder }}</app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.lead_source_id && !selectingLeadSource" (click)="selectLeadSource()" [class.field-error]="hasFieldError('lead_source_id')">
            <input matInput type="text" placeholder="Lead Source" [value]="selectedLeadSource?.name || ''" readonly />
            <mat-hint *ngIf="hasFieldError('lead_source_id')" class="error-hint">{{ getFieldError('lead_source_id') }}</mat-hint>
          </mat-form-field>
          <app-lead-source-auto-complete *ngIf="edited && policy.lead_source_id && selectingLeadSource" clearable field="name"
            (selected)="leadSourceSelected($event)" (cleared)="leadSourceCleared()">
          </app-lead-source-auto-complete>

          <app-text label="Responsible Seller" *ngIf="!edited || !policy.responsible_seller_id">{{ data.responsible_seller?.name || placeholder }}</app-text>

          <mat-form-field floatLabel="always" *ngIf="edited && policy.responsible_seller_id && !selectingResponsibleSeller" (click)="selectResponsibleSeller()" [class.field-error]="hasFieldError('responsible_seller_id')">
            <input matInput type="text" placeholder="Responsible Seller" [value]="selectedResponsibleSeller?.name || ''" readonly />
            <mat-hint *ngIf="hasFieldError('responsible_seller_id')" class="error-hint">{{ getFieldError('responsible_seller_id') }}</mat-hint>
          </mat-form-field>
          <app-partner-auto-complete *ngIf="edited && policy.responsible_seller_id && selectingResponsibleSeller" clearable field="name"
            [current]="selectedResponsibleSeller"
            (selected)="responsibleSellerSelected($event)"
            (cleared)="responsibleSellerCleared()">
          </app-partner-auto-complete>

        </div>
      </div>

    </form>

    <form [formGroup]="finalReportForm" id="invoice-info-v2-final-report-form" (ngSubmit)="saveFinalReport()" novalidate>
      <div class="card-section-container" *ngIf="finalReportEdited">
        <div class="card-section">
          <mat-form-field>
            <input matInput type="email" placeholder="Final Report Email Recipients" formControlName="recipients" />
            <mat-hint>Leave empty to generate only. Separate multiple addresses with comma (,).</mat-hint>
          </mat-form-field>
        </div>
      </div>
    </form>

    <form [formGroup]="offerForm" id="invoice-info-v2-offer-form" (ngSubmit)="saveOffer()" novalidate>
      <div class="card-section-container" *ngIf="offerEdited">
        <div class="card-section">
          <mat-form-field>
            <input matInput type="email" placeholder="Offer Email Recipients" formControlName="recipients" />
            <mat-hint>Leave empty to generate only. Separate multiple addresses with comma (,).</mat-hint>
          </mat-form-field>
        </div>
      </div>
    </form>

    <!--
      Transitional Action menu carried over from invoice-info-widget.
      "Send to Subfinancing" is the automated counterpart of the retained
      "Generate Subfinancing Invoice" manual backup: it appears only for an
      invoice whose configuration is a subfinancing provider (that object also
      supplies the provider name in the label) and only for a user holding
      invoice:send-subfinancing. Generation is gated on invoice-docs:generate;
      the server enforces both, the *ngIf is the usability half.
      "Generate Invoice Number" and "Advance Invoice Number" are the two
      modern number actions: generate mints the Corona invoice number and
      reference when the invoice has none and is safe to click repeatedly,
      advance is the deliberate ordinal bump behind a dialog that requires a
      reason — recorded as a priority comment on the invoice — and only
      appears once a number exists. Both need invoice-docs:generate, which
      the server enforces on their routes.
      Long-term home is issue #247.
      "Generate Offer" goes through cctool-go's generate-offer endpoint,
      whose state advance is guarded.
    -->
    <mat-menu #action="matMenu">
      <button mat-menu-item type="button" (click)="generateFinalReport()">Generate Final Report</button>
      <button mat-menu-item type="button" *ngIf="canGenerateDocs$ | async" (click)="generateInvoiceNumber()">Generate Invoice Number</button>
      <button
        mat-menu-item
        type="button"
        *ngIf="(canGenerateDocs$ | async) && invoice?.new_number"
        (click)="advanceInvoiceNumber()"
      >
        Advance Invoice Number
      </button>
      <button mat-menu-item type="button" (click)="generateOffer()">Generate Offer</button>
      <button mat-menu-item type="button" *ngIf="canGenerateDocs$ | async" (click)="generateSubfinancingInvoice()">Generate Subfinancing Invoice</button>
      <!-- While a financing hold applies, the send is disabled with the holds
           as its tooltip (add-financing-holds): the send asks the provider
           to finance the invoice. The wrapper carries the tooltip because a
           disabled button emits no pointer events. -->
      <span
        class="send-subfinancing-action"
        *ngIf="(canSendSubfinancing$ | async) && invoice?.subfinancing"
        [matTooltip]="subfinancingHoldTooltip()"
        [attr.tabindex]="invoice?.financing_on_hold ? 0 : null"
      >
        <button
          mat-menu-item
          type="button"
          [disabled]="invoice?.financing_on_hold"
          (click)="sendToSubfinancing()"
        >
          Send to Subfinancing ({{ invoice?.subfinancing?.provider_name }})
        </button>
      </span>
      <button mat-menu-item type="button" (click)="sendInvoice()">Send Invoice</button>
      <button mat-menu-item type="button" *ngIf="canSendInvoice$ | async" [matMenuTriggerFor]="sendInvoiceNewMenu">Send Invoice (new)</button>
      <button mat-menu-item type="button" (click)="sendModifiedOriginalInvoice()">Send Modified Original Invoice</button>
      <button mat-menu-item type="button" (click)="sendToAccounting()">Send to Accounting</button>
    </mat-menu>

    <!--
      The "(new)" Send Invoice routes through the modern
      /api/invoices/{id}/send endpoint (Apix verkkolasku send). The mode
      submenu mirrors the legacy Options A/B: e-invoice merges the
      watermarked original and routes to the end customer's eInvoice
      address; paper sends the Corona invoice only and forces print
      delivery. Gated on the invoice:send permission the backend enforces
      on those endpoints, whichever role granted it.
    -->
    <mat-menu #sendInvoiceNewMenu="matMenu">
      <button mat-menu-item type="button" (click)="sendInvoiceNew('einvoice')">As e-invoice</button>
      <button mat-menu-item type="button" (click)="sendInvoiceNew('paper')">As paper letter</button>
    </mat-menu>

    <mat-card-actions align="end" *ngIf="finalReportEdited">
      <button type="submit" mat-button [disabled]="!finalReportForm.valid" form="invoice-info-v2-final-report-form">Generate</button>
      <button type="button" mat-button (click)="cancelFinalReport()">Cancel</button>
    </mat-card-actions>

    <mat-card-actions align="end" *ngIf="offerEdited">
      <button type="submit" mat-button [disabled]="!offerForm.valid" form="invoice-info-v2-offer-form">Generate</button>
      <button type="button" mat-button (click)="cancelOffer()">Cancel</button>
    </mat-card-actions>

    <mat-card-actions align="end" *ngIf="!offerEdited && !finalReportEdited">
      <button type="button" mat-button *ngIf="!edited" [disabled]="locked" [matMenuTriggerFor]="action">Action</button>
      <!--
        Edit needs invoice:edit-information on top of the state gate; the
        permission narrows the gate, never replaces it. Without it the button
        stays visible but disabled. A disabled button gets no pointer events
        and takes no focus, so the tooltip saying why sits on this wrapper.
        Only once the permission is known to be missing is the tooltip enabled
        and the wrapper a named tab stop, so a keyboard user gets the reason
        too: the tooltip opens on keyboard focus and describes the wrapper.
        Until then its message is empty, since MatTooltip describes its host
        even while disabled, and a permitted user's screen reader would
        otherwise hear the refusal next to an enabled Edit.
        The ngIf holds one object so every binding reads one subscription,
        and `=== false` / `!== true` are deliberate: the permission is
        unknown, not missing, until the current user loads.
      -->
      <ng-container *ngIf="{ allowed: canEditInformation$ | async } as editGate">
        <span
          *ngIf="!edited"
          class="edit-action"
          [matTooltip]="editGate.allowed === false ? editPermissionTooltip : ''"
          [matTooltipDisabled]="editGate.allowed !== false"
          [attr.tabindex]="editGate.allowed === false ? 0 : null"
          [attr.role]="editGate.allowed === false ? 'group' : null"
          [attr.aria-label]="editGate.allowed === false ? 'Edit' : null"
        >
          <button type="button" mat-button [disabled]="locked || !policy.allowed || editGate.allowed !== true" (click)="edit()">Edit</button>
        </span>
      </ng-container>
      <button type="submit" mat-button *ngIf="edited" [disabled]="!form.valid || saving" form="invoice-info-v2-form">Save</button>
      <button type="reset" mat-button *ngIf="edited" (click)="revert()">Cancel</button>
    </mat-card-actions>
    </ng-container>
  </ng-container>

</app-card-widget>
